import { apiClient } from '@realtyfeed/data-access';
import { normalisePermissionGrant } from '../permissions/session-permissions';

/**
 * The session's permission grant.
 *
 * ### Where it actually comes from
 *
 * `GET /rf-backend/staff-permissions`, authorised with the session's token. **Not** the id
 * token's `permissions` claim, which is what the helper named `getPermissionList()` in the
 * legacy app reads — that one feeds a different Redux slice (`state.roles`) which is consumed
 * by `canRole()` for route filtering that filters nothing, because no legacy route declares
 * `permissions`. The gating that actually happens — the sidebar, `PermissionWrapper`,
 * `usePermission` — reads `state.user.permissions`, and that is set from this endpoint.
 *
 * Getting this wrong is not a small error: the id-token claim and this response are both
 * "an object keyed by permission name", so a wrong source produces a plausible-looking set
 * that gates the wrong things. See docs/07-decisions.md D15.
 *
 * ### The response shape
 *
 * An object keyed by permission name with a truthy value — `{"can_get_customers": true}` — the
 * same shape `canRole` and the legacy `usePermission` index into. `normalisePermissionGrant`
 * also accepts an array of names and fails closed on anything else.
 */
const STAFF_PERMISSIONS_PATH = '/rf-backend/staff-permissions';

/**
 * Fetches the grant. Returns an empty set on failure rather than throwing.
 *
 * Failing closed is the safe direction: an empty grant hides gated screens, which is
 * recoverable and visible. The alternative — treating a failed fetch as "allow" — would show
 * every screen to everyone, and the only thing standing between that and a mistake would be the
 * backend's own 403s.
 *
 * `silent` because the caller decides how to surface it; a toast on app bootstrap fires before
 * there is anything to read it against.
 */
export const fetchStaffPermissions = async (): Promise<ReadonlySet<string>> => {
  try {
    const response = await apiClient.get<unknown>(STAFF_PERMISSIONS_PATH, { silent: true });
    return normalisePermissionGrant(response.data);
  } catch {
    return normalisePermissionGrant(null);
  }
};
